import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { adminGetProjects, adminAddProject, adminUpdateProject, adminDeleteProject } from '../../api/admin';
import './AdminProjects.css';

function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    short_desc: '',
    sort_order: 0,
    is_active: 1
  });
  const [coverImage, setCoverImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const res = await adminGetProjects();
    if (res.success) setProjects(res.data || []);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');

    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('category', form.category || '');
      formData.append('location', form.location || '');
      formData.append('description', form.description || '');
      formData.append('short_desc', form.short_desc || '');
      formData.append('sort_order', form.sort_order || 0);
      formData.append('is_active', form.is_active || 1);
      if (coverImage) {
        formData.append('cover_image', coverImage);
      }

      if (editing) {
        formData.append('id', editing.id);
      }

      const res = editing 
        ? await adminUpdateProject(formData)
        : await adminAddProject(formData);
      
      if (res.success) {
        setMsg('✅ Project saved successfully!');
        setForm({ title: '', category: '', location: '', description: '', short_desc: '', sort_order: 0, is_active: 1 });
        setCoverImage(null);
        setPreview('');
        setEditing(null);
        setShowForm(false);
        fetchProjects();
      } else {
        setMsg('❌ Error: ' + res.message);
      }
    } catch (error) {
      setMsg('❌ Network error. Please try again.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    const res = await adminDeleteProject(id);
    if (res.success) fetchProjects();
  };

  const handleEdit = (project) => {
    setEditing(project);
    setForm({
      title: project.title || '',
      category: project.category || '',
      location: project.location || '',
      description: project.description || '',
      short_desc: project.short_desc || '',
      sort_order: project.sort_order || 0,
      is_active: project.is_active || 1
    });
    setPreview(project.cover_image || '');
    setShowForm(true);
  };

  const openAddForm = () => {
    setEditing(null);
    setForm({ title: '', category: '', location: '', description: '', short_desc: '', sort_order: 0, is_active: 1 });
    setCoverImage(null);
    setPreview('');
    setShowForm(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Projects">
        <div className="adm-loading">Loading projects...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Projects">
      <div className="adm-page-header">
        <h2>Manage Projects</h2>
        <button className="adm-btn-primary" onClick={openAddForm}>
          + Add Project
        </button>
      </div>

      {msg && (
        <div className={`adm-msg ${msg.includes('Error') ? 'adm-msg--error' : 'adm-msg--success'}`}>
          {msg}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="adm-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{editing ? 'Edit Project' : 'Add New Project'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="adm-modal__body">
              <form onSubmit={handleSubmit}>
                <div className="adm-form-field">
                  <label>Project Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                  />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label>Category</label>
                    <input
                      type="text"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder="e.g. Residential"
                    />
                  </div>
                  <div className="adm-form-field">
                    <label>Location</label>
                    <input
                      type="text"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      placeholder="e.g. Raipur"
                    />
                  </div>
                </div>

                <div className="adm-form-field">
                  <label>Short Description</label>
                  <input
                    type="text"
                    value={form.short_desc}
                    onChange={(e) => setForm({ ...form, short_desc: e.target.value })}
                    placeholder="Brief description"
                  />
                </div>

                <div className="adm-form-field">
                  <label>Full Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows="4"
                    placeholder="Detailed project description..."
                  />
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label>Sort Order</label>
                    <input
                      type="number"
                      value={form.sort_order}
                      onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="adm-form-field">
                    <label>Status</label>
                    <select
                      value={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="adm-form-field">
                  <label>Cover Image</label>
                  <div className="adm-upload-box" onClick={() => document.getElementById('coverInput').click()}>
                    {preview ? (
                      <img src={preview} alt="Preview" className="adm-upload-preview" />
                    ) : (
                      <div className="adm-upload-placeholder">📷 Click to upload image</div>
                    )}
                  </div>
                  <input
                    id="coverInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setCoverImage(file);
                        setPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                </div>

                <div className="adm-modal__footer">
                  <button type="button" className="adm-btn-secondary" onClick={() => setShowForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="adm-btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editing ? 'Update Project' : 'Save Project'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="adm-section">
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No projects added yet.
                  </td>
                </tr>
              )}
              {projects.map((project) => (
                <tr key={project.id}>
                  <td>
                    {project.cover_image ? (
                      <img
                        src={project.cover_image}
                        alt={project.title}
                        className="adm-thumb"
                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }}
                        onError={(e) => {
                          e.target.src = '';
                          e.target.alt = 'Image not found';
                        }}
                      />
                    ) : (
                      <span style={{ color: '#999', fontSize: 12 }}>No image</span>
                    )}
                  </td>
                  <td>
                    <strong>{project.title}</strong>
                    {project.short_desc && (
                      <div style={{ fontSize: 12, color: '#888' }}>{project.short_desc}</div>
                    )}
                  </td>
                  <td>{project.category || '—'}</td>
                  <td>{project.location || '—'}</td>
                  <td>
                    <span className={`adm-status ${project.is_active ? 'adm-status--active' : 'adm-status--inactive'}`}>
                      {project.is_active ? '✅ Active' : '❌ Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="adm-actions">
                      <button className="adm-btn-edit" onClick={() => handleEdit(project)}>
                        ✏️ Edit
                      </button>
                      <button className="adm-btn-delete" onClick={() => handleDelete(project.id)}>
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminProjects;